// src/pages/sow-analysis/UploadSowModal.jsx
//
// "Analyze SOWs" modal -- engagement metadata + a multi-file dropzone,
// with an upload/analysis progress bar.
//
// Account and Project are read from the workspace's own switcher context
// (useWorkspace(), via useSowAnalysisPage()) rather than typed here --
// Project can still be overridden for this one analysis via the inline
// Select, or a new one registered on the spot via the same
// RegisterProjectModal Draft SOW already uses.

import PropTypes from "prop-types";
import { Plus, ClipboardCheck, Unlink } from "lucide-react";
import { useState, useEffect } from "react";
import { fetchSowAnalysisPlanningAvailability } from "../../api/sowApi";
import { Alert, Button, FileDropzone, FormField, Input, Modal, Select } from "../../shared/ui";
import { CONTRACT_TYPES } from "./useSowAnalysisPage";

function ProgressBar({ progress, statusText }) {
  const pct = Math.round(Math.min(100, Math.max(0, progress)));
  return (
    <div className="flex flex-col gap-1.5">
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-brand-600 transition-all duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-muted">
        <span>{statusText}</span>
        <span>{pct}%</span>
      </div>
    </div>
  );
}


function PlanningSowOption({ engagementId, usePlanningSow, setUsePlanningSow, busy }) {
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!engagementId) {
      setAvailability(null);
      return;
    }
    fetchSowAnalysisPlanningAvailability(engagementId)
      .then((data) => { if (!cancelled) setAvailability(data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [engagementId]);

  if (!availability?.available) return null;

  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-brand-100 bg-brand-50 px-4 py-3">
      <ClipboardCheck size={16} className="text-brand-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800">
          Planning already has a drafted SOW for this engagement - <span className="font-medium">{availability.title}</span>.
        </p>
      </div>
      {usePlanningSow ? (
        <Button size="sm" variant="secondary" onClick={() => setUsePlanningSow(false)} disabled={busy} type="button">
          <Unlink size={14} className="mr-1 inline" /> Unlink
        </Button>
      ) : (
        <Button size="sm" onClick={() => setUsePlanningSow(true)} disabled={busy} type="button">
          Import from Planning
        </Button>
      )}
    </div>
  );
}

export default function UploadSowModal({
  open,
  onClose,
  onSubmit,
  accountName,
  engagements,
  selectedEngagementId,
  projectId,
  onEngagementChange,
  canCreateProject,
  onNewProject,
  opportunityLinked,
  form,
  onFormChange,
  files,
  onFilesChange,
  busy,
  progress,
  statusText,
  onImportPlanningSow,
  error,
}) {
  const [usePlanningSow, setUsePlanningSow] = useState(false);

  useEffect(() => {
    if (open) setUsePlanningSow(false);
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (usePlanningSow) {
      if (onImportPlanningSow) onImportPlanningSow();
    } else {
      onSubmit(e);
    }
  };

  return (
    <Modal
      open={open}
      title="Analyze SOWs"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="sow-upload-form" loading={busy}>
            {busy ? "Analyzing..." : "Analyze SOWs"}
          </Button>
        </>
      }
    >
      <form id="sow-upload-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Account">
            <Input value={accountName || "—"} disabled />
          </FormField>
          <FormField label="Project" required>
            <div className="flex gap-2">
              <Select
                options={engagements.map((e) => ({ value: e.engagement_id, label: e.client_name }))}
                value={selectedEngagementId || ""}
                onChange={(e) => onEngagementChange(e.target.value)}
              />
              {canCreateProject && (
                <Button type="button" variant="secondary" size="sm" onClick={onNewProject}>
                  <Plus size={14} /> New
                </Button>
              )}
            </div>
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Opportunity ID"
            helperText={opportunityLinked ? "From the Project's linked Opportunity -- editable" : "Optional -- e.g. 006xxxxxxxxxxxxx"}
          >
            <Input name="sfOpportunityId" value={form.sfOpportunityId} onChange={onFormChange} />
          </FormField>
          <FormField label="Contract Type">
            <Select
              name="contractType"
              options={CONTRACT_TYPES.map((ct) => ({ value: ct, label: ct }))}
              value={form.contractType}
              onChange={onFormChange}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Stage"
            helperText={opportunityLinked ? "From the Project's linked Opportunity -- editable" : "Optional -- e.g. Closed Won / Closed Lost"}
          >
            <Input name="stage" value={form.stage} onChange={onFormChange} />
          </FormField>
          <FormField label="SL / GD Email" helperText={opportunityLinked ? "From the Project's linked Opportunity -- editable" : "Optional"}>
            <Input name="slGdEmail" value={form.slGdEmail} onChange={onFormChange} />
          </FormField>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField label="SOW Drafter" helperText={opportunityLinked ? "From the Project's linked Opportunity -- editable" : "Optional"}>
            <Input name="sowDrafter" value={form.sowDrafter} onChange={onFormChange} />
          </FormField>
          <FormField label="SOW Reviewer" helperText={opportunityLinked ? "From the Project's linked Opportunity -- editable" : "Optional"}>
            <Input name="sowReviewer" value={form.sowReviewer} onChange={onFormChange} />
          </FormField>
        </div>

        {!usePlanningSow && (
          <FormField label="SOW Files" required helperText="PDF or DOCX, one or more">
            <FileDropzone
              multiple
              accept={[".pdf", ".doc", ".docx"]}
              files={files}
              onChange={onFilesChange}
            />
          </FormField>
        )}

        <PlanningSowOption engagementId={selectedEngagementId} usePlanningSow={usePlanningSow} setUsePlanningSow={setUsePlanningSow} busy={busy} />

        {(busy || progress > 0) && <ProgressBar progress={progress} statusText={statusText} />}
      </form>
    </Modal>
  );
}

UploadSowModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  accountName: PropTypes.string,
  engagements: PropTypes.array.isRequired,
  selectedEngagementId: PropTypes.string,
  onEngagementChange: PropTypes.func.isRequired,
  canCreateProject: PropTypes.bool,
  onNewProject: PropTypes.func.isRequired,
  opportunityLinked: PropTypes.bool,
  form: PropTypes.object.isRequired,
  onFormChange: PropTypes.func.isRequired,
  files: PropTypes.array.isRequired,
  onFilesChange: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
  progress: PropTypes.number.isRequired,
  statusText: PropTypes.string,
  onImportPlanningSow: PropTypes.func,
  error: PropTypes.string,
};
